/**
 * Utility functions for Clinic Visibility and filtering.
 */

/**
 * Determines whether a clinic should be hidden from the main widget UI.
 * 
 * Rules:
 * 1. Hard rule: Do NOT consider `is_hidden_from_main_widget` in partner (partnerId) case.
 * 2. In non-partner case, hide clinic if `is_hidden_from_main_widget` is true / 1 / "1" / "true".
 * 3. Checks direct clinic property, lookup map (allClinicDataByID), or allowed clinic list (allClinicData).
 *
 * @param {Object} clinic - Clinic object from API
 * @param {string|number|null} partnerId - Partner identifier, if present
 * @param {Object|Array|null} allClinicData - Allowed clinic list or lookup map
 * @returns {boolean} True if the clinic should be hidden from the main widget UI
 */
export const isClinicHiddenFromMainWidget = (clinic, partnerId, allClinicData = null) => {
  if (!clinic) return false;
  // Hard rule: don't consider this in partner (partnerId) case
  if (partnerId) return false;

  // Direct check on clinic object
  const val = clinic?.is_hidden_from_main_widget;
  if (val === true || val === 1 || val === '1' || val === 'true') {
    return true;
  }

  const clinicId = String(clinic?.id ?? clinic?.clinic_id);

  if (allClinicData) {
    // If passed a map (allClinicDataByID object)
    if (typeof allClinicData === 'object' && !Array.isArray(allClinicData) && !Array.isArray(allClinicData?.data)) {
      const matched = allClinicData[clinicId] || allClinicData[Number(clinicId)];
      if (matched) {
        const lookupVal = matched?.is_hidden_from_main_widget;
        if (lookupVal === true || lookupVal === 1 || lookupVal === '1' || lookupVal === 'true') {
          return true;
        }
      }
    }

    // If passed a list of allowed clinics (allClinicData array or { data: [...] })
    let allowedList = [];
    if (Array.isArray(allClinicData)) {
      allowedList = allClinicData;
    } else if (Array.isArray(allClinicData?.data)) {
      allowedList = allClinicData.data;
    }

    if (allowedList.length > 0) {
      const found = allowedList.find((c) => String(c.id ?? c.clinic_id) === clinicId);
      if (!found) {
        // Not present in allowed list -> hidden
        return true;
      }
      const foundVal = found?.is_hidden_from_main_widget;
      if (foundVal === true || foundVal === 1 || foundVal === '1' || foundVal === 'true') {
        return true;
      }
    }
  }

  return false;
};
